import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';

const { FiTag, FiPlus, FiEdit2, FiTrash2, FiCheck, FiX } = FiIcons;

const ContactTagSettings = () => {
  const { contactTags, setContactTags } = useData();
  const [newTag, setNewTag] = useState('');
  const [editingTag, setEditingTag] = useState(null);
  const [editValue, setEditValue] = useState('');

  const addTag = () => {
    if (newTag.trim() && !contactTags.includes(newTag.trim())) {
      setContactTags([...contactTags, newTag.trim()]);
      setNewTag('');
    }
  };

  const startEdit = (tag) => {
    setEditingTag(tag);
    setEditValue(tag);
  };

  const saveEdit = () => {
    if (editValue.trim() && !contactTags.includes(editValue.trim())) {
      setContactTags(contactTags.map(tag => 
        tag === editingTag ? editValue.trim() : tag
      ));
    }
    setEditingTag(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingTag(null);
    setEditValue('');
  };

  const deleteTag = (tagToDelete) => {
    if (window.confirm(`Are you sure you want to delete the "${tagToDelete}" tag?`)) {
      setContactTags(contactTags.filter(tag => tag !== tagToDelete));
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Contact Tags</h2>
        <p className="text-gray-600">Manage tags to categorize your contacts.</p>
      </div>

      {/* Add New Tag */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-800 mb-3">Add New Tag</h3>
        <div className="flex space-x-3">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => handleKeyPress(e, addTag)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter tag name..."
          />
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={addTag}
            disabled={!newTag.trim() || contactTags.includes(newTag.trim())}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} />
            <span>Add Tag</span>
          </motion.button>
        </div>
      </div>

      {/* Existing Tags */}
      <div>
        <h3 className="text-lg font-medium text-gray-800 mb-3">Current Tags</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <AnimatePresence>
            {contactTags.map((tag) => (
              <motion.div
                key={tag}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
              >
                {editingTag === tag ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyPress={(e) => handleKeyPress(e, saveEdit)}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoFocus
                    />
                    <div className="flex space-x-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={saveEdit}
                        className="flex-1 bg-green-100 text-green-700 py-1 px-2 rounded text-sm hover:bg-green-200 transition-colors flex items-center justify-center space-x-1"
                      >
                        <SafeIcon icon={FiCheck} className="text-xs" />
                        <span>Save</span>
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={cancelEdit}
                        className="flex-1 bg-gray-100 text-gray-700 py-1 px-2 rounded text-sm hover:bg-gray-200 transition-colors flex items-center justify-center space-x-1"
                      >
                        <SafeIcon icon={FiX} className="text-xs" />
                        <span>Cancel</span>
                      </motion.button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiTag} className="text-gray-400" />
                      <span className="font-medium text-gray-800">{tag}</span>
                    </div>
                    <div className="flex space-x-1">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => startEdit(tag)}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <SafeIcon icon={FiEdit2} className="text-sm" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => deleteTag(tag)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} className="text-sm" />
                      </motion.button>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {contactTags.length === 0 && (
          <div className="text-center py-8">
            <SafeIcon icon={FiTag} className="text-4xl text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">No tags created yet. Add your first tag above.</p>
          </div>
        )}
      </div>

      {/* Usage Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">How to use tags:</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Use tags to categorize your contacts (e.g., Lead, Client, Partner)</li>
          <li>• Tags help you filter and organize your contact list</li>
          <li>• You can assign multiple tags to each contact</li>
          <li>• Default tags include: Lead, Client, Prospect, Partner</li>
        </ul>
      </div>
    </div>
  );
};

export default ContactTagSettings;